// Boiler Plate Imports
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import API from "../../api";
import colors from "../../theme/colors";

// Conatiner Component Imports
import { Button, Card } from "@mui/material";
// import Table from "../../components/Table";
import { TableComponent, ItemLink } from "../../components/Table/dataGridPro";
import TableHeader from "../../components/Page/tableHeader";

// Page Component Imports
import { Link, useParams } from "react-router-dom";
import SparklineComponent from "../../components/HighCharts/sparkline";
import TableSubHeader from "../Page/tableSubHeader";

const Table = React.memo(TableComponent);

const ActiveAlarmsTagNone = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.grayLight};
  color: ${colors.grayDark};
  border: 1px solid ${colors.gray};
  border-radius: 15px;
  padding: 2px;
  width: 60px;
`;

const ActiveAlarmsTagSome = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.redLightError};
  color: ${colors.redError};
  border: 1px solid ${colors.redError};
  border-radius: 15px;
  padding: 2px;
  width: 60px;
  font-weight: bold;
`;

const StatusIcon = styled.div`
  display: block;
  height: 8px;
  width: 8px;
  border-radius: 8px;
  background: ${(props) => props.$statusColor};
`;

const StatusTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  &:before {
    content: "";
    display: block;
    height: 8px;
    width: 8px;
    border-radius: 8px;
    background: ${(props) => props.$statusColor};
  }
`;

const TanksTableWidget = ({
  locationId,
  showAddTankButton,
  showAllTanksButton,
  enableMoveTanks = false,
  moveTanksCallback = () => {},
  subHeader,
  isAdminView = false,
  initalState,
  doneLoadingData = () => {},
  reloadData = false,
}) => {
  const userProfile = useSelector((state) => state.user);
  const { organizationId: orgId } = useParams();
  const [organizationId, setOrganizationId] = useState(orgId);

  const [disableMoveTanksButton, setDisableMoveTanksButton] = useState(true);
  const [selectedTankIds, setSelectedTankIds] = useState([]);
  const [tanks, setTanks] = useState([]);

  let tankTableColumns = useMemo(() => {
    let _columns = [
      {
        headerName: "Tank Name",
        field: "name",
        display: "flex",
        valueGetter: (value, row) => {
          return row.name;
        },
        renderCell: ({ field, row }) => {
          let color = colors.greenSuccess;
          if (row.status === "Maintenance") {
            color = colors.orange;
          } else if (row.status === "Deactivated") {
            color = colors.redError;
          }
          return (
            <ItemLink to={`/${row.organizationId}/tanks/${row.id}`}>
              <StatusIcon $statusColor={color} /> {row[field]}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Weight Trend 24h",
        field: "weightTrend",
        sortable: false,
        width: 170,
        renderCell: ({ row }) => {
          return (
            <SparklineComponent seriesData={row.weightTrend24hrSeriesData} />
          );
        },
      },
      {
        headerName: "Alarms",
        field: "activeAlarmsCount",
        display: "flex",
        type: "number",
        valueGetter: (value, row) => {
          return row.activeAlarmsCount;
        },
        renderCell: ({ row }) => {
          if (row.activeAlarmsCount === 0) {
            return <ActiveAlarmsTagNone>none</ActiveAlarmsTagNone>;
          } else {
            return (
              <ActiveAlarmsTagSome>{row.activeAlarmsCount}</ActiveAlarmsTagSome>
            );
          }
        },
      },
      {
        headerName: "Filled %",
        field: "percentFull",
        type: "number",
        valueGetter: (value, row) => {
          return row.percentFull;
        },
        renderCell: ({ row }) => {
          if (row?.percentFull !== undefined) {
            return `${row?.percentFull}%`;
          } else {
            return "";
          }
        },
      },
      {
        headerName: "Current Weight (kg)",
        field: "currentWeight",
        type: "number",
        valueGetter: (value, row) => {
          return row.currentWeight;
        },
        renderCell: ({ row }) => {
          if (row?.currentWeight !== undefined) {
            return `${parseFloat(row?.currentWeight).toFixed(2)} kg`;
          } else {
            return "";
          }
        },
      },
      {
        headerName: "Low Weight (kg)",
        field: "alarmLowLevelWeightKg",
        type: "number",
        valueGetter: (value, row) => {
          return row.alarmLowLevelWeightKg;
        },
        renderCell: ({ row }) => {
          if (row?.alarmLowLevelWeightKg !== undefined) {
            return `${row.alarmLowLevelWeightKg} kg`;
          } else {
            return "";
          }
        },
      },
      {
        headerName: "Full Weight (kg)",
        field: "fullWeightKG",
        type: "number",
        valueGetter: (value, row) => {
          return row.fullWeightKG;
        },
        renderCell: ({ row }) => {
          if (row?.fullWeightKG !== undefined) {
            return `${row.fullWeightKG} kg`;
          }
        },
      },
      {
        headerName: "24hr Evap (kg/day)",
        field: "evapRate",
        type: "number",
        valueGetter: (value, row) => {
          return row.evapRate || 0;
        },
        renderCell: ({ row }) => {
          if (row?.evapRate !== undefined) {
            return `${row.evapRate} kg/day`;
          } else {
            return null;
          }
        },
      },
      {
        headerName: "Current Temp (°C)",
        field: "currentTemperature",
        type: "number",
        valueGetter: (value, row) => {
          return row.currentTemperature || 0;
        },
        renderCell: ({ row }) => {
          if (row?.currentTemperature !== undefined) {
            return `${row.currentTemperature} °C`;
          } else {
            return null;
          }
        },
      },
      {
        headerName: "Low Temp (°C)",
        field: "alarmTempRangeLow",
        type: "number",
        valueGetter: (value, row) => {
          return row.alarmTempRangeLow || 0;
        },
        renderCell: ({ row }) => {
          if (row?.alarmTempRangeLow !== undefined) {
            return `${row.alarmTempRangeLow} °C`;
          } else {
            return null;
          }
        },
      },
      {
        headerName: "Battery %",
        field: "batteryPercentage",
        type: "number",
        valueGetter: (value, row) => {
          return row.batteryPercentage || 0;
        },
        renderCell: ({ row }) => {
          if (row.batteryPercentage) {
            return `${Math.round(row.batteryPercentage)}%`;
          } else {
            return null;
          }
        },
      },
      {
        headerName: "Data Last Received",
        field: "dataLastReceivedObject",
        type: "dateTime",
      },
      {
        headerName: "Last Fill Date",
        field: "lastFillDateObject",
        type: "dateTime",
      },
      {
        headerName: "Last Fill (kg)",
        field: "lastFillAmountKg",
        type: "number",
        valueGetter: (value, row) => {
          return row?.lastFillAmountKg || 0;
        },
        valueFormatter: (value, row) => {
          if (row?.lastFillAmountKg !== undefined) {
            return `${parseFloat(row?.lastFillAmountKg).toFixed(2)} kg`;
          }
        },
      },
      {
        headerName: "Location",
        field: "locationName",
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${row.organizationId}/locations/${row.locationId}`}>
              {row.locationName}
            </ItemLink>
          );
        },
      },

      {
        headerName: "Contents",
        field: "contentsDisplayName",
      },
      {
        headerName: "Make",
        field: "makeDisplayName",
      },
      {
        headerName: "Model",
        field: "modelDisplayName",
      },
      {
        headerName: "Type",
        field: "typeDisplayName",
      },
      {
        headerName: "Status",
        field: "statusDescription",
        width: 115,
        valueGetter: (value, row) => {
          return row.status;
        },
        renderCell: ({ row }) => {
          let color = colors.greenSuccess;
          if (row.status === "Maintenance") {
            color = colors.orange;
          } else if (row.status === "Deactivated") {
            color = colors.redError;
          }

          return <StatusTag $statusColor={color}>{row.status}</StatusTag>;
        },
      },
      {
        headerName: "Install Date",
        field: "installDateObject",
        type: "dateTime",
      },
      {
        headerName: "Scale ID",
        field: "scaleId",
      },
      {
        headerName: "Created",
        field: "dateCreateObject",
        type: "dateTime",
      },
    ];
    if (isAdminView) {
      _columns = [
        {
          headerName: "Organization",
          field: "organizationName",
          valueGetter: (value, row) => {
            return row.organizationName;
          },
          renderCell: ({ row }) => {
            return (
              <ItemLink to={`/${row.organizationId}/organization`}>
                {row.organizationName}
              </ItemLink>
            );
          },
        },
        {
          headerName: "Device",
          field: "deviceName",
          valueGetter: (value, row) => {
            return row.deviceName;
          },
          renderCell: ({ row }) => {
            return (
              <ItemLink to={`/admin/devices/${row.deviceId}`}>
                {row.deviceName}
              </ItemLink>
            );
          },
        },
        ..._columns,
      ];
    }
    return _columns;
  }, [isAdminView]);

  const dataLayer = {
    GetOrganizationTanks: async ({ organizationId }) => {
      let tanks = [];
      if (isAdminView) {
        tanks = await API.Tank.TanksGetAll();
      } else {
        tanks = await API.Tank.TanksGet({ organizationId });
        if (locationId) {
          tanks = tanks.filter((t) => t.locationId === locationId);
        }
      }

      const tankRows = tanks.map((tank) => {
        return {
          ...tank,
          status: (() => {
            if (tank.deleted) {
              return "Deactivated";
            } else if (tank.serviceStatus === "active") {
              return "Active";
            } else if (tank.serviceStatus === "maintenance") {
              return "Maintenance";
            }
          })(),
          organizationName: (() => {
            return tank?.organization?.name;
          })(),
          locationName: tank.location.name,
          activeAlarmsCount: tank.activeAlarmsCount,
          dateCreateObject: new Date(tank.created),
          installDateObject: new Date(tank.installDate),
          currentWeight: tank.latestScaleData?.currentWeight,
          currentTemperature: tank.latestScaleData?.currentTemperature,
          batteryPercentage: tank.latestScaleData?.batteryPercentage,
          dataLastReceivedObject: tank.latestScaleData?.timestamp
            ? new Date(tank.latestScaleData?.timestamp * 1000)
            : "",
          lastFillDateObject: tank?.lastFillDate
            ? new Date(tank?.lastFillDate)
            : "",
          evapRate: (() => {
            if (tank?.latestScaleData?.rateOfLoss) {
              const rolKgPerDay = tank?.latestScaleData?.rateOfLoss / 1000;
              return Math.round(rolKgPerDay * 100) / 100;
            }
          })(),
        };
      });
      setTanks(tanks);
      doneLoadingData(tankRows);
      return tankRows;
    },
  };

  const handlers = {
    moveSelectedTanks: () => {
      moveTanksCallback(
        selectedTankIds,
        tanks.filter((t) => selectedTankIds.includes(t.id)),
      );
    },
  };

  return (
    <Card>
      <TableHeader>
        <div>
          <h2>Tanks</h2>
          <TableSubHeader>{subHeader}</TableSubHeader>
        </div>

        <div>
          {showAddTankButton &&
            userProfile?.role &&
            userProfile.role !== "TECH" && (
              <Button
                component={Link}
                to={"add"}
                // onClick={handlers.addTankClick}
                variant="contained"
                color="primary"
                size="large"
              >
                ADD TANK
              </Button>
            )}
          {showAllTanksButton && (
            <Button
              component={Link}
              to={`/${organizationId}/tanks`}
              variant="outlined"
              color="primary"
              size="small"
            >
              VIEW TANKS
            </Button>
          )}

          {enableMoveTanks && (
            <Button
              component={Link}
              onClick={handlers.moveSelectedTanks}
              variant="contained"
              color="primary"
              size="small"
              disabled={disableMoveTanksButton}
            >
              MOVE SELECTED TANKS
            </Button>
          )}
        </div>
      </TableHeader>
      <Table
        isAdminView={isAdminView}
        columns={tankTableColumns}
        autoResizeColumns={false}
        reloadData={reloadData}
        label={""}
        placeholder="There are no tanks created yet"
        getRowsFunction={dataLayer.GetOrganizationTanks}
        initialState={{
          pinnedColumns: { left: ["name"] },
          sorting: {
            sortModel: [
              { field: "activeAlarmsCount", sort: "desc" },
              { field: "name", sort: "asc" },
            ],
          },
          ...initalState,
        }}
        disableMultipleSelection={true}
        checkboxSelection={enableMoveTanks}
        rowSelectionCallback={(ids) => {
          console.log("Selected Tank IDs:", ids);
          setSelectedTankIds(ids);
          if (ids.length > 0) {
            setDisableMoveTanksButton(false);
          } else {
            setDisableMoveTanksButton(true);
          }
        }}
        rowSelectionModelInitValues={[]}
      />
    </Card>
  );
};

export default React.memo(TanksTableWidget);
